import { CONFIG, formatTemperatureDegree } from '@hero/core';
import { TempIcon } from '@hero/web-icons';
import { SelectAuto, SelectAutoGroup } from '@hero/web-components';
import { useTranslation } from 'react-i18next';

interface OrderTemperatureSelectorProps {
  /** Current selected temperature ID (as string) */
  value?: string;
  /** Callback when temperature changes */
  onChange: (value: string) => void;
  /** Optional placeholder text */
  placeholder?: string;
  /** Optional className */
  className?: string;
  /** Optional disabled state */
  disabled?: boolean;
}

export function OrderTemperatureSelector({
  value,
  onChange,
  placeholder,
  className,
  disabled = false,
}: OrderTemperatureSelectorProps) {
  const { t } = useTranslation(['m-order']);

  const chilledTemps = CONFIG.order.temperatureList.filter(temp =>
    temp.types.includes('chilled'),
  );
  const frozenTemps = CONFIG.order.temperatureList.filter(temp =>
    temp.types.includes('frozen'),
  );

  const toOption = (temp: (typeof CONFIG.order.temperatureList)[number]) => ({
    value: temp.id.toString(),
    label: formatTemperatureDegree(temp) || '',
    icon: <TempIcon className="w-5 h-5 text-blue-500" />,
  });

  const options: SelectAutoGroup[] = [
    {
      label: t('m-order:temperature_types.chilled', 'Chilled'),
      options: chilledTemps.map(toOption),
    },
    {
      label: t('m-order:temperature_types.frozen', 'Frozen'),
      options: frozenTemps.map(toOption),
    },
  ];

  return (
    <SelectAuto
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder || t('m-order:temperature_types.select', 'Temperature')}
      disabled={disabled}
      className={className}
      triggerClassName="text-[16px] font-[600] leading-[24px]"
    />
  );
}
